import { useEffect, useCallback, useRef } from 'react';
import useTimeout from 'hooks/useTimeout';

/**
 * These are the events we consider to be an user "activity", i.e. not idling.
 * Copied from react-idle-timer.
 */
const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'wheel',
  'DOMMouseScroll',
  'mouseWheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'MSPointerDown',
  'MSPointerMove',
] as const;

// Attach the above listeners to `document`
const addActivityListener = (handler: EventListener): void => {
  ACTIVITY_EVENTS.forEach(name => {
    document.addEventListener(name, handler, { passive: true });
  });
};

// Attach the above listeners to `document`
const removeActivityListener = (handler: EventListener): void => {
  ACTIVITY_EVENTS.forEach(name => {
    document.removeEventListener(name, handler);
  });
};

type IdleTimerHook = (
  onIdle: Function,
  timeout: number,
  /**
   * Whether to use the timer
   * Note that the timer will start from the beginning when switching from
   * false to true (i.e., this is not a pause function)
   */
  enabled?: boolean,
) => [
  // Start timer
  () => void,
  // Stop timer
  () => void,
];

const useIdleTimer: IdleTimerHook = (onIdle, timeout, enabled = true) => {
  const [setTimeout, clearTimeout] = useTimeout();
  const activityListenerRef = useRef<EventListener | null>(null);

  const cleanup = useCallback(() => {
    if (activityListenerRef.current) {
      removeActivityListener(activityListenerRef.current);
      activityListenerRef.current = null;
    }
  }, [activityListenerRef]);

  const startTimer = useCallback(() => {
    cleanup();
    if (!enabled) {
      return;
    }

    const onActivity = (): void => {
      setTimeout(() => {
        cleanup();
        onIdle();
      }, timeout);
    };

    onActivity();
    addActivityListener(onActivity);
    activityListenerRef.current = onActivity;
  }, [setTimeout, onIdle, timeout, cleanup, enabled]);

  const stopTimer = useCallback(() => {
    cleanup();
    clearTimeout();
  }, [cleanup, clearTimeout]);

  useEffect(() => {
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return [startTimer, stopTimer];
};

export default useIdleTimer;
